'use client';

// Зачем: WebRTC P2P-транспорт для Mystery Room. Хост держит RTCPeerConnection per peer + DataChannel,
// гость держит одно соединение к хосту. SDP/ICE проходит через PHP signaling (p2pSignaling.ts).
// Сообщения внутри DataChannel — JSON {type, payload}.

import {
  mrP2pCreate,
  mrP2pFetchRtcConfiguration,
  mrP2pInfo,
  mrP2pJoin,
  mrP2pPoll,
  mrP2pResumeHost,
  mrP2pSendSignal,
  type MrP2PCreateOptions,
  type MrP2PSignalMessage,
} from './p2pSignaling';

import type {
  MysteryRoomBotDifficulty,
  MysteryRoomGameMode,
  MysteryRoomGlobalMissionKey,
  MysteryRoomInventoryItemState,
} from '../core/localRuntime';

export type MrP2PRole = 'host' | 'guest';

export type MrP2PMessage =
  | {
      type: 'welcome';
      payload: {
        selfId: string;
        hostId: string;
        mapId: string;
        botCount: number;
        botsOnly: boolean;
        gameMode: MysteryRoomGameMode;
        botDifficulty: MysteryRoomBotDifficulty;
        globalMission: MysteryRoomGlobalMissionKey;
        zombieWaveCount?: number;
        zombieWaveSize?: number;
      };
    }
  | { type: 'state'; payload: MrP2PWorldState }
  | { type: 'interact'; payload: { interactableId: string; tsMs: number } }
  | { type: 'door'; payload: { doorId: string; tsMs: number } }
  | { type: 'shoot'; payload: { fromPeerId: string; targetX: number; targetY: number; tsMs: number } }
  | { type: 'inventory_init'; payload: { items: MysteryRoomInventoryItemState[]; tsMs: number } }
  | { type: 'input'; payload: MrP2PInputMessage }
  | { type: 'chat'; payload: { author: string; body: string; tsMs: number } }
  | { type: 'match-end'; payload: { reason: string; results: unknown } }
  | { type: 'leave'; payload: { peerId: string } };

export type MrP2PInputMessage = {
  peerId: string;
  move: { x: number; y: number };
  pointerTarget: { x: number; y: number } | null;
  facing?: 'up' | 'down' | 'left' | 'right';
  position?: { x: number; y: number };
  tsMs: number;
};

export type MrP2PRemoteActor = {
  peerId: string;
  displayName: string;
  isBot: boolean;
  position: { x: number; y: number };
  facing: 'up' | 'down' | 'left' | 'right';
  velocity: { x: number; y: number };
  animationState: 'idle' | 'walk';
  color: number;
  health: number;
  maxHealth: number;
  alive: boolean;
};

/** Компактный снимок NPC с хоста — перезаписывает позу/анимацию у гостя */
export type MrP2PNpcSnapshot = {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  facing: 'up' | 'down' | 'left' | 'right';
  animationState: 'idle' | 'walk';
  alive: boolean;
  zoneId: string;
};

export type MrP2PLightSnapshot = {
  id: string;
  enabled: boolean;
};

/** Двери: достаточно для отображения и коллизий (без точного тайминга анимации). */
export type MrP2PDoorSnapshot = {
  id: string;
  state: 'closed' | 'opening' | 'open' | 'closing' | 'locked';
  openProgress: number;
  holdOpenMs: number;
};

export type MrP2PTriggerSnapshot = {
  id: string;
  lastActivatedAtMs: number | null;
};

export type MrP2PHazardSnapshot = {
  id: string;
  kind: 'smoke' | 'sparks';
  zoneId: string;
  position: { x: number; y: number };
  radius: number;
  intensity: number;
};

export type MrP2PWorldAlarmSnapshot = {
  state: 'clear' | 'active';
  sourceIncidentId: string | null;
  activeUntilMs: number;
};

/** Снимок мира с хоста (герой гостя применяет одним блоком). */
export type P2pAuthoritativeExtraWorld = {
  doors: MrP2PDoorSnapshot[];
  triggers: MrP2PTriggerSnapshot[];
  hazards: MrP2PHazardSnapshot[];
  worldAlarm: MrP2PWorldAlarmSnapshot;
};

/** P2P: выстрел гостя — обрабатывает только хост */
export type P2pHostShootAction = { fromPeerId: string; targetX: number; targetY: number; tsMs: number };

/** Очередь на хосте: дверь / interactable / выстрел (поля выстрела см. P2pHostShootAction). */
export type P2pHostIncomingAction = Partial<P2pHostShootAction> & {
  doorId?: string;
  interactableId?: string;
  guestPeerId?: string;
};

export type MrP2PWorldState = {
  serverNowMs: number;
  hostId: string;
  actors: MrP2PRemoteActor[];
  /** Синхронизация NPC карты с хоста (гость накладывает на локальный рантайм) */
  npcs?: MrP2PNpcSnapshot[];
  /** id объектов со state === 'used' на хосте (подборы и т.п.) */
  usedInteractableIds?: string[];
  /** Состояние источников света с хоста */
  lights?: MrP2PLightSnapshot[];
  /** Двери, триггеры и побочные эффекты с хоста (грубый снимок, без точного времени) */
  doors?: MrP2PDoorSnapshot[];
  triggers?: MrP2PTriggerSnapshot[];
  hazards?: MrP2PHazardSnapshot[];
  worldAlarm?: MrP2PWorldAlarmSnapshot;
  /** Инвентари гостей (peerId → предметы), авторитет хоста */
  guestInventories?: Record<string, MysteryRoomInventoryItemState[]>;
};

export type MrP2PHostHandle = {
  role: 'host';
  lobbyId: string;
  hostId: string;
  selfId: string;
  send: (msg: MrP2PMessage) => void;
  sendTo: (peerId: string, msg: MrP2PMessage) => void;
  onPeerJoin: (cb: (peerId: string, displayName: string) => void) => () => void;
  onPeerLeave: (cb: (peerId: string) => void) => () => void;
  onMessage: (cb: (peerId: string, msg: MrP2PMessage) => void) => () => void;
  close: (opts?: { destroySignaling?: boolean }) => Promise<void>;
};

export type MrP2PGuestHandle = {
  role: 'guest';
  lobbyId: string;
  hostId: string;
  selfId: string;
  send: (msg: MrP2PMessage) => void;
  onMessage: (cb: (msg: MrP2PMessage) => void) => () => void;
  onConnected: (cb: () => void) => () => void;
  onDisconnected: (cb: (reason: string) => void) => () => void;
  close: (_opts?: { destroySignaling?: boolean }) => Promise<void>;
};

function safeParse(raw: string): MrP2PMessage | null {
  try {
    const data = JSON.parse(raw);
    if (data && typeof data.type === 'string') return data as MrP2PMessage;
    return null;
  } catch {
    return null;
  }
}

function makeListenerHub<TArgs extends unknown[]>(): {
  add: (cb: (...args: TArgs) => void) => () => void;
  emit: (...args: TArgs) => void;
} {
  const cbs = new Set<(...args: TArgs) => void>();
  return {
    add(cb) {
      cbs.add(cb);
      return () => cbs.delete(cb);
    },
    emit(...args) {
      cbs.forEach((cb) => {
        try {
          cb(...args);
        } catch (err) {
          console.warn('[p2p] listener failed', err);
        }
      });
    },
  };
}

// ===== HOST =====

async function runMrP2pHostSession(lobbyId: string, hostId: string, hostToken: string): Promise<MrP2PHostHandle> {
  const rtcConfig = await mrP2pFetchRtcConfiguration();

  type PeerSlot = {
    peerId: string;
    displayName: string;
    pc: RTCPeerConnection;
    dc: RTCDataChannel | null;
    pendingIce: RTCIceCandidateInit[];
  };
  const peers = new Map<string, PeerSlot>();
  const onJoin = makeListenerHub<[string, string]>();
  const onLeave = makeListenerHub<[string]>();
  const onMessage = makeListenerHub<[string, MrP2PMessage]>();

  let lastMs = 0;
  let pollAbort: AbortController | null = null;
  let stopped = false;
  let pollBackoffMs = 1500;
  /** Зачем: не звать mrP2pClose дважды (жёсткий выход + cleanup). */
  let signalingFileClosed = false;

  // Зачем: отдельный peer-connection per гость, чтобы хост был автором всех offer'ов.
  async function ensurePeer(peerId: string, displayName: string): Promise<PeerSlot> {
    const existing = peers.get(peerId);
    if (existing) return existing;
    const pc = new RTCPeerConnection(rtcConfig);
    const dc = pc.createDataChannel('mr-game', { ordered: true });
    const slot: PeerSlot = { peerId, displayName, pc, dc, pendingIce: [] };
    peers.set(peerId, slot);

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      void mrP2pSendSignal(lobbyId, hostId, hostToken, peerId, 'candidate', e.candidate.toJSON());
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        if (peers.has(peerId)) {
          peers.delete(peerId);
          onLeave.emit(peerId);
        }
      }
    };
    dc.onopen = () => onJoin.emit(peerId, displayName);
    dc.onclose = () => {
      if (peers.has(peerId)) {
        peers.delete(peerId);
        onLeave.emit(peerId);
      }
    };
    dc.onmessage = (e) => {
      const msg = typeof e.data === 'string' ? safeParse(e.data) : null;
      if (msg) onMessage.emit(peerId, msg);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await mrP2pSendSignal(lobbyId, hostId, hostToken, peerId, 'offer', offer);
    return slot;
  }

  async function handleSignal(msg: MrP2PSignalMessage) {
    const peerId = msg.fromId;
    if (peerId === hostId) return;
    const slot = peers.get(peerId);
    if (msg.type === 'answer') {
      if (slot && msg.payload && typeof msg.payload === 'object') {
        await slot.pc.setRemoteDescription(msg.payload as RTCSessionDescriptionInit);
        for (const cand of slot.pendingIce.splice(0)) {
          try {
            await slot.pc.addIceCandidate(cand);
          } catch (err) {
            console.warn('[p2p host] addIceCandidate failed', err);
          }
        }
      }
    } else if (msg.type === 'candidate') {
      const cand = msg.payload as RTCIceCandidateInit | null;
      if (!cand || !slot) return;
      if (slot.pc.remoteDescription) {
        try {
          await slot.pc.addIceCandidate(cand);
        } catch (err) {
          console.warn('[p2p host] addIceCandidate failed', err);
        }
      } else {
        slot.pendingIce.push(cand);
      }
    } else if (msg.type === 'leave') {
      if (slot) {
        slot.pc.close();
        peers.delete(peerId);
        onLeave.emit(peerId);
      }
    }
  }

  async function pollLoop() {
    while (!stopped) {
      pollAbort = new AbortController();
      try {
        const res = await mrP2pPoll(lobbyId, hostId, lastMs, 4, pollAbort.signal);
        lastMs = res.lastMs || lastMs;
        // Зачем: новые гости появляются через peers — открываем им peer-соединение, если ещё нет.
        for (const peer of res.peers) {
          if (!peers.has(peer.peerId)) {
            await ensurePeer(peer.peerId, peer.userName || peer.peerId);
          }
        }
        for (const m of res.messages) {
          await handleSignal(m);
        }
        pollBackoffMs = 1500;
      } catch (err) {
        if (stopped) break;
        console.warn('[p2p host] poll failed', err);
        await new Promise((resolve) => setTimeout(resolve, pollBackoffMs));
        pollBackoffMs = Math.min(12_000, Math.round(pollBackoffMs * 1.5) + Math.floor(Math.random() * 400));
      }
    }
  }

  void pollLoop();

  return {
    role: 'host',
    lobbyId,
    hostId,
    selfId: hostId,
    send(msg) {
      const data = JSON.stringify(msg);
      peers.forEach((slot) => {
        if (slot.dc?.readyState === 'open') {
          try {
            slot.dc.send(data);
          } catch (err) {
            console.warn('[p2p host] send failed', err);
          }
        }
      });
    },
    sendTo(peerId, msg) {
      const slot = peers.get(peerId);
      if (slot?.dc?.readyState === 'open') {
        try {
          slot.dc.send(JSON.stringify(msg));
        } catch (err) {
          console.warn('[p2p host] sendTo failed', err);
        }
      }
    },
    onPeerJoin: onJoin.add,
    onPeerLeave: onLeave.add,
    onMessage: onMessage.add,
    async close(opts?: { destroySignaling?: boolean }) {
      const destroySignaling = opts?.destroySignaling !== false;
      stopped = true;
      pollAbort?.abort();
      peers.forEach((slot) => {
        try {
          slot.dc?.close();
        } catch {}
        try {
          slot.pc.close();
        } catch {}
      });
      peers.clear();
      if (!destroySignaling || signalingFileClosed) return;
      signalingFileClosed = true;
      try {
        const { mrP2pClose } = await import('./p2pSignaling');
        await mrP2pClose(lobbyId, hostToken);
      } catch (err) {
        console.warn('[p2p host] close lobby failed', err);
      }
    },
  };
}

export async function mrP2pStartHost(options: MrP2PCreateOptions): Promise<MrP2PHostHandle> {
  const created = await mrP2pCreate(options);
  return runMrP2pHostSession(created.lobbyId, created.hostId, created.hostToken);
}

/** Зачем: после F5 / новой вкладки с ?matchId= — снова стать хостом с тем же lobbyId (тот же аккаунт). */
export async function mrP2pStartHostResume(lobbyId: string): Promise<MrP2PHostHandle> {
  const r = await mrP2pResumeHost(lobbyId);
  return runMrP2pHostSession(r.lobbyId, r.hostId, r.hostToken);
}

// ===== GUEST =====

export async function mrP2pStartGuest(lobbyId: string): Promise<MrP2PGuestHandle> {
  const info = await mrP2pInfo(lobbyId);
  const join = await mrP2pJoin(lobbyId);
  const peerId = join.peerId;
  const peerToken = join.peerToken;
  const hostId = info.hostId;
  const rtcConfig = await mrP2pFetchRtcConfiguration();

  const pc = new RTCPeerConnection(rtcConfig);
  let dc: RTCDataChannel | null = null;
  const onMessage = makeListenerHub<[MrP2PMessage]>();
  const onConnected = makeListenerHub<[]>();
  const onDisconnected = makeListenerHub<[string]>();
  const pendingIce: RTCIceCandidateInit[] = [];
  let lastMs = 0;
  let pollAbort: AbortController | null = null;
  let stopped = false;
  let pollBackoffMs = 1500;

  pc.onicecandidate = (e) => {
    if (!e.candidate) return;
    void mrP2pSendSignal(lobbyId, peerId, peerToken, hostId, 'candidate', e.candidate.toJSON());
  };
  pc.ondatachannel = (e) => {
    dc = e.channel;
    dc.onopen = () => onConnected.emit();
    dc.onclose = () => onDisconnected.emit('datachannel_closed');
    dc.onmessage = (ev) => {
      const msg = typeof ev.data === 'string' ? safeParse(ev.data) : null;
      if (msg) onMessage.emit(msg);
    };
  };
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === 'failed') onDisconnected.emit('connection_failed');
    if (pc.connectionState === 'closed') onDisconnected.emit('connection_closed');
  };

  async function handleSignal(msg: MrP2PSignalMessage) {
    if (msg.fromId !== hostId) return;
    if (msg.type === 'offer') {
      await pc.setRemoteDescription(msg.payload as RTCSessionDescriptionInit);
      for (const cand of pendingIce.splice(0)) {
        try {
          await pc.addIceCandidate(cand);
        } catch (err) {
          console.warn('[p2p guest] addIceCandidate failed', err);
        }
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await mrP2pSendSignal(lobbyId, peerId, peerToken, hostId, 'answer', answer);
    } else if (msg.type === 'candidate') {
      const cand = msg.payload as RTCIceCandidateInit | null;
      if (!cand) return;
      if (pc.remoteDescription) {
        try {
          await pc.addIceCandidate(cand);
        } catch (err) {
          console.warn('[p2p guest] addIceCandidate failed', err);
        }
      } else {
        pendingIce.push(cand);
      }
    } else if (msg.type === 'leave') {
      onDisconnected.emit('host_left');
    }
  }

  async function pollLoop() {
    while (!stopped) {
      pollAbort = new AbortController();
      try {
        const res = await mrP2pPoll(lobbyId, peerId, lastMs, 4, pollAbort.signal);
        lastMs = res.lastMs || lastMs;
        for (const m of res.messages) {
          await handleSignal(m);
        }
        pollBackoffMs = 1500;
      } catch (err) {
        if (stopped) break;
        console.warn('[p2p guest] poll failed', err);
        await new Promise((resolve) => setTimeout(resolve, pollBackoffMs));
        pollBackoffMs = Math.min(12_000, Math.round(pollBackoffMs * 1.5) + Math.floor(Math.random() * 400));
      }
    }
  }

  void pollLoop();

  return {
    role: 'guest',
    lobbyId,
    hostId,
    selfId: peerId,
    send(msg) {
      if (dc?.readyState === 'open') {
        try {
          dc.send(JSON.stringify(msg));
        } catch (err) {
          console.warn('[p2p guest] send failed', err);
        }
      }
    },
    onMessage: onMessage.add,
    onConnected: onConnected.add,
    onDisconnected: onDisconnected.add,
    async close(_opts?: { destroySignaling?: boolean }) {
      stopped = true;
      pollAbort?.abort();
      try {
        await mrP2pSendSignal(lobbyId, peerId, peerToken, hostId, 'leave', null);
      } catch {}
      try {
        dc?.close();
      } catch {}
      try {
        pc.close();
      } catch {}
    },
  };
}
